import { useState, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsTelephone, BsEnvelope, BsCalendar, BsFileText, BsArrowRepeat, BsStars, BsCheckCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import IconButton from '#globalComponents/IconButton/IconButton';
import { STALE_TIMES } from '#lib/queryConstants';
import { useErrorStore } from '#lib/stores/errorStore';

import { generateNextAction, acceptSuggestion, dismissSuggestion } from '../../../api/crmApi';

const ACTION_ICONS = {
	call: BsTelephone,
	email: BsEnvelope,
	meeting: BsCalendar,
	note: BsFileText,
};

const NextBestAction = ({ dealId }) => {
	const qc = useQueryClient();
	const showError = useErrorStore((s) => s.showError);
	const [dismissed, setDismissed] = useState(false);

	const {
		data,
		isLoading,
		error: queryError,
		refetch,
		isFetching,
	} = useQuery({
		queryKey: ['crm', 'nextAction', dealId],
		queryFn: () => generateNextAction(dealId),
		staleTime: STALE_TIMES.STANDARD,
		enabled: Boolean(dealId) && !dismissed,
		retry: false,
	});

	const hasPlannedActivities = queryError?.code === 'CRM_DEAL_HAS_PLANNED_ACTIVITIES';
	const suggestion = data?.suggestion ?? null;

	const handleAccept = useCallback(async () => {
		if (!suggestion?.id) return;
		try {
			await acceptSuggestion(suggestion.id);
			qc.invalidateQueries({ queryKey: ['crm', 'nextAction', dealId] });
			qc.invalidateQueries({ queryKey: ['crm', 'activities'] });
		} catch (err) {
			showError({ error: err, title: 'Aktion konnte nicht geplant werden' });
		}
	}, [suggestion, dealId, qc, showError]);

	const handleDismiss = useCallback(async () => {
		if (suggestion?.id) {
			try {
				await dismissSuggestion(suggestion.id);
			} catch {
				/* ignore */
			}
		}
		setDismissed(true);
	}, [suggestion]);

	const handleRegenerate = useCallback(() => {
		setDismissed(false);
		refetch();
	}, [refetch]);

	if (dismissed && !isFetching) return null;
	if (isLoading || isFetching) {
		return (
			<Card className="relative overflow-hidden p-0 bg-primary-100 border-primary-200">
				<div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-400 animate-pulse" />
				<div className="px-4 py-5 text-center text-xs text-bgBase-800">
					<BsStars className="w-4 h-4 mx-auto mb-1 text-primary-400 animate-spin" />
					KI-Vorschlag wird generiert …
				</div>
			</Card>
		);
	}
	if (hasPlannedActivities) {
		return (
			<Card className="bg-bgBase-300 px-4 py-3.5 flex items-center gap-3">
				<BsCheckCircle className="w-4 h-4 text-success-500 shrink-0" />
				<div>
					<div className="text-[13px] font-medium text-text">Geplante Aktionen vorhanden</div>
					<div className="text-[11px] text-bgBase-800 mt-0.5">
						Schließe bestehende Aktionen ab, um einen neuen KI-Vorschlag zu erhalten.
					</div>
				</div>
			</Card>
		);
	}
	if (!suggestion) return null;

	const IconComponent = ACTION_ICONS[suggestion.actionType] ?? BsFileText;

	return (
		<Card className="relative overflow-hidden p-0 bg-primary-100 border-primary-200">
			<div
				className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-400"
				style={{
					backgroundSize: '200% 100%',
					animation: 'nbaShimmer 4s linear infinite',
				}}
			/>
			<style>{`@keyframes nbaShimmer { to { background-position: 200% 0; } }`}</style>

			<div className="flex items-center justify-between px-4 py-2.5 border-b border-primary-200">
				<div className="flex items-center gap-2.5">
					<div className="w-6 h-6 rounded-control bg-gradient-to-br from-primary-400 to-primary-600 text-white grid place-items-center">
						<BsStars className="w-3 h-3" />
					</div>
					<div>
						<div className="text-[13px] font-semibold text-text">Nächste beste Aktion</div>
						<div className="text-[10px] text-bgBase-800 mt-0.5">
							KI-Vorschlag basierend auf Deal-Historie
						</div>
					</div>
				</div>
				<IconButton
					icon={BsArrowRepeat}
					label="Neu generieren"
					tone="primary"
					onClick={handleRegenerate}
					className="h-7 w-7"
				/>
			</div>

			<div className="grid gap-3.5 px-4 py-3.5" style={{ gridTemplateColumns: '36px 1fr auto' }}>
				<div className="w-9 h-9 rounded-card bg-gradient-to-br from-primary-500 to-primary-600 text-white grid place-items-center">
					<IconComponent className="w-4 h-4" />
				</div>
				<div className="min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="text-sm font-semibold text-text">{suggestion.title}</span>
						{suggestion.winRateLift && (
							<span className="text-[10px] font-semibold bg-success-200 text-success-700 px-1.5 py-0.5 rounded-full">
								{suggestion.winRateLift}
							</span>
						)}
					</div>
					{suggestion.reasoning && (
						<p className="text-xs text-bgBase-800 leading-relaxed mt-1">{suggestion.reasoning}</p>
					)}
					{suggestion.inputContext?.emailTemplateName && (
						<span className="inline-flex items-center gap-1 text-[10px] text-primary-500 bg-primary-200 px-1.5 py-0.5 rounded-full mt-1">
							<BsFileText className="w-2.5 h-2.5" /> {suggestion.inputContext.emailTemplateName}
						</span>
					)}
					<div className="flex items-center gap-1 text-[10px] text-bgBase-800 mt-1.5">
						{suggestion.dueAt && (
							<span className="inline-flex items-center gap-1">
								<BsCalendar className="w-2.5 h-2.5" />{' '}
								{new Intl.DateTimeFormat('de-DE', {
									day: '2-digit',
									month: 'short',
									hour: '2-digit',
									minute: '2-digit',
								}).format(new Date(suggestion.dueAt))}
							</span>
						)}
						{suggestion.duration && <span>· {suggestion.duration}</span>}
					</div>
				</div>
				<div className="flex flex-col gap-1 items-end">
					<Button onClick={handleAccept} size="sm">
						Planen
					</Button>
					<button
						type="button"
						onClick={handleDismiss}
						className="px-2.5 py-1.5 text-[11px] text-bgBase-800 hover:text-text transition-colors"
					>
						Verwerfen
					</button>
				</div>
			</div>
		</Card>
	);
};

NextBestAction.propTypes = {
	dealId: PropTypes.string.isRequired,
};

export default NextBestAction;
